// ─── Den ganzen Baum ins Bild holen ──────────────────────────────────────────
//
// Die Rechnung hinter „Ansicht zentrieren". Sie stand inline in der Ansicht und
// hat dort zweimal hintereinander einen Fehler getragen:
//
//   1. Bei noch ungemessener Fläche (0×0) kam ein NEGATIVER Maßstab heraus,
//      und d3 machte daraus `translate(NaN,NaN) scale(NaN)`. Ab da war die
//      Fläche dauerhaft hin – jede weitere Zoom-Aktion interpoliert von NaN
//      aus, auch „Ansicht zentrieren" kam nicht mehr heraus.
//   2. Die Abhilfe dagegen war zu grob: sie klemmte den Maßstab auf MIN_ZOOM
//      (10 %) hoch. Ein Baum mit dreihundert Personen braucht aber rund 3 %,
//      um ganz aufs Bild zu passen – seither wurde er beim Zentrieren
//      abgeschnitten.
//
// Deshalb steht sie jetzt hier: eine reine Funktion, die sich ohne Browser
// prüfen lässt. Die Untergrenze des Zoom-Verhaltens gilt für das, was der
// NUTZER dreht – nicht für das Einpassen. d3 lässt `zoom.transform` bewusst an
// scaleExtent vorbei, und genau davon lebt diese Ansicht.

/** Das Rechteck, in das der Baum passen soll. */
export interface Flaeche {
  width: number;
  height: number;
}

export interface Einpassung {
  /** Maßstab. */
  k: number;
  x: number;
  y: number;
}

/** Halbe Knotenmaße – der Rand, den ein Kästchen um seinen Mittelpunkt braucht. */
const HALB_BREIT = 90;
const HALB_HOCH = 50;

/** Luft zum Rand, damit die äußeren Kästchen nicht am Bildrand kleben. */
const RAND = 60;

/**
 * Nicht weiter als 120 % heranfahren.
 *
 * Ohne diese Grenze würde ein Baum aus zwei Personen bildschirmfüllend
 * aufgeblasen – korrekt gerechnet, aber unbrauchbar anzusehen.
 */
const MAX_HERAN = 1.2;

/**
 * Wie die Ansicht stehen muss, damit alle Knoten hineinpassen.
 *
 * Gibt `null` zurück, wenn sich nichts Sinnvolles berechnen lässt: keine
 * Knoten, oder eine Fläche, die noch nicht gemessen ist. Der Aufrufer versucht
 * es dann erneut, sobald die Maße stehen – ein Ergebnis „irgendwas" wäre hier
 * schlimmer als keins.
 */
export function passeEin(
  knoten: readonly { x: number; y: number }[],
  flaeche: Flaeche,
): Einpassung | null {
  if (knoten.length === 0) return null;
  if (!(flaeche.width > 2 * RAND) || !(flaeche.height > 2 * RAND)) return null;

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const k of knoten) {
    if (!Number.isFinite(k.x) || !Number.isFinite(k.y)) continue;
    minX = Math.min(minX, k.x - HALB_BREIT);
    maxX = Math.max(maxX, k.x + HALB_BREIT);
    minY = Math.min(minY, k.y - HALB_HOCH);
    maxY = Math.max(maxY, k.y + HALB_HOCH);
  }

  // Alle Knoten unbrauchbar (kann bei einem gescheiterten Layout vorkommen).
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;

  const breite = maxX - minX;
  const hoehe = maxY - minY;
  const mitteX = (minX + maxX) / 2;
  const mitteY = (minY + maxY) / 2;

  const k = Math.min(
    (flaeche.width - 2 * RAND) / breite,
    (flaeche.height - 2 * RAND) / hoehe,
    MAX_HERAN,
  );

  // Bewusst KEINE Untergrenze: ein Baum mit dreihundert Personen braucht rund
  // 3 %, und „ganz zeigen" heißt ganz. Abgefangen wird nur, was kaputt ist.
  if (!Number.isFinite(k) || k <= 0) return null;

  return {
    k,
    x: flaeche.width / 2 - mitteX * k,
    y: flaeche.height / 2 - mitteY * k,
  };
}
